<script>
import bus from "@/bus";
import Button from "@/components/Button";
import Captcha from "@/components/Captcha";
import ErrorMessage from "@/components/ErrorMessage";
import TextInput from "@/components/TextInput";
import TownSelection from "@/components/TownSelection";
import { useI18n } from "vue-i18n";
import { useUserStore } from "../../stores/user";
const statusCodes = {
  200: "views.modals.contact.200",
  422: "views.modals.contact.422",
  502: "views.modals.contact.502",
};

export default {
  setup() {
    const userStore = useUserStore();
    const { t } = useI18n();
    return { userStore, t };
  },
  name: "Contact",
  components: {
    TextInput,
    ErrorMessage,
    TownSelection,
    Button,
    Captcha,
  },
  props: {},
  data() {
    const userStore = useUserStore();
    return {
      communeDropdownExpanded: false,
      firstname: userStore.getData.firstname,
      lastname: userStore.getData.lastname,
      towns: [],
      email: userStore.getData.email,
      subject: "",
      content: "",
      errors: [],
      sendState: 0,
      sendStateEnum: {
        sending: 0,
        sent: 1,
      },
      valid: true,
    };
  },
  computed: {
    sendButton() {
      // sendState values:
      // 0: Not sent yet
      // 1: Sent
      if (this.sendState === this.sendStateEnum.sent) {
        return {
          icon: "spinner",
          text: "views.modals.contact.sends.1",
          color: "gray-700",
        };
      }
      return {
        icon: "paper-plane",
        text: "views.modals.contact.sends.2",
        color: "airbnbRed",
      };
    },
  },
  methods: {
    async contact() {
      this.sendState = this.sendStateEnum.sent;
      const { firstname, lastname, towns, email, subject, content, valid } =
        this;
      const error = await this.userStore.contact({
        firstname,
        lastname,
        towns,
        email,
        subject,
        content,
        valid,
      });
      this.sendState = this.sendStateEnum.sending;
      if (error) {
        const { message } = error.data;
        this.errors.push({
          id: error.headers.date,
          message: statusCodes[error.status] || message,
        });
      } else {
        this.$toast.add({
          severity: "success",
          summary: this.$t("views.modals.contact.success"),
          detail: this.$t(statusCodes[200]),
          life: 3000,
        });
        bus.emit("closeModal");
      }
    },
    closeMessage(id) {
      Object.keys(this.errors).forEach((key) => {
        if (id === this.errors[key].id) {
          this.errors.splice(key, 1);
        }
      });
    },
  },
};
</script>

<template>
  <div class="p-8 z-50 bg-white shadow-xl rounded sm:min-w-md sm:max-w-2xl max-h-screen overflow-y-auto">
    <h3 class="mb-2 text-lg text-center text-lightGrey">
      <font-awesome-icon icon="envelope" class="inline-block mx-auto text-airbnbRed text-xl" />&nbsp;{{
        t("views.modals.contact.title") }}
    </h3>
    <br />

    <form @submit.prevent="contact">
      <div class="flex flex-row">
        <TextInput id="lastname" v-model="lastname" :label="t('views.modals.contact.last_name')" type="text"
          class="mb-4 pr-2 flex-1" />
        <TextInput id="firstname" v-model="firstname" :label="t('views.modals.contact.first_name')" type="text"
          class="mb-4 pl-2 flex-1" />
      </div>

      <label class="block mb-2 text-lightGrey text-sm font-bold">{{
        t("views.modals.contact.commune_name")
        }}</label>
      <TownSelection />
      <p class="text-xs text-lightGrey leading-normal mb-4">
        {{ t("views.modals.contact.info") }}
      </p>
      <div v-for="msg in errors" :key="msg.id">
        <ErrorMessage :value="msg" @input="closeMessage" />
      </div>
      <TextInput id="email" v-model="email" :label="t('views.modals.contact.email')" type="email" class="mb-4" />

      <label class="block mb-2 text-lightGrey text-sm font-bold">{{
        t("views.modals.contact.object")
        }}</label>
      <select id="subject" v-model="subject" required
        class="w-full py-2 px-3 mb-4 appearance-none border rounded text-lightGrey bg-white leading-tight outline-none\ focus:border focus:border-solid focus:border-airbnbRed focus:shadow">
        <option value="default" selected disabled>
          {{ t("views.modals.contact.select_object") }}
        </option>
        <option v-for="e in 8" :key="e">
          {{ t('views.modals.contact.subjects.' + e) }}
        </option>
      </select>

      <label class="block mb-2 text-lightGrey text-sm font-bold">
        {{ t("views.modals.contact.message") }}
      </label>
      <textarea id="content" v-model="content"
        class="w-full py-2 px-3 mb-4 appearance-none border rounded text-lightGrey leading-tight outline-none\ focus:border focus:border-solid focus:border-airbnbRed focus:shadow placeholder-gray-700"
        style="max-height: 320px; min-height: 80px" rows="5" type="type"
        :placeholder="t('views.modals.contact.content')" required />
      <Captcha v-model="valid" />

      <div class="w-full flex flex-items-center justify-between">
        <p class="text-xs text-lightGrey leading-normal">
          {{ t("views.modals.contact.required") }}
        </p>
        <Button class="text-sm flex flex-row" type="submit" :bg="sendButton.color"
          :disabled="sendState === sendStateEnum.sent">
          <font-awesome-icon :icon="sendButton.icon" class="mr-1" :spin="sendState === 1" />
          <span>{{ t(sendButton.text) }}</span>
        </Button>
      </div>
    </form>
  </div>
</template>
<style scoped></style>
